import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useQuery } from "react-query";
import {
  composeLeg,
  formatScores,
  statusTypes,
  useMatchData,
  composeAggregate
} from "../../../mls-match-list/utils";
import MatchInfo from "../../../mls-match-list/components/MatchInfo";
import Sponsor from "../../../shared/components/Sponsor";
import ToggleButton from "../../../shared/components/ToggleButton";
import TicketIcon from "../../../shared/svg/TicketIcon";
import PlayCircleIcon from "../../../shared/svg/PlayCircleIcon";
import MatchScoreBox from "../../../mls-match-list/components/MatchScoreBox";
import ReminderIcon from "../../../shared/svg/ReminderIcon";
import MatchData from "../../../mls-match-list/components/MatchData/MatchData";
import { formatPlayerSummaryData } from "../../utils";
import HubMatchClub from "../HubMatchClub/HubMatchClub";
import { useReactAppState } from "../../../shared/containers/utils";
import { modalActionTypes, modalTypes } from "../../../shared/hooks/useModalOptions";
import MatchModal from "../../../mls-match-list/components/MatchModal/MatchModal";
import {
  apiURL,
  d3AggregateAPI,
  d3PlayerAPI,
  forgeMatchVideosAPI,
  mlsMatchCommentariesAPI,
  forgeMatchContentVideosAPI
} from "../../../shared/api/variables";
import { fetchClient } from "../../../shared/api/fetching";
import { highlightTypes, matchHighlightsTagSlug } from "../../../shared/utils";
import { composeVideoList } from "../../../mls-match-feed/utils";
import { trackCustomSnowplowEvent } from '../../../../libraries/_modules/tracking/snowplow/snowplowEventTrackers';
import { composeMLSMatchContextData, createMatchContext, createUiInteractionPrimaryEvent } from '../../../../libraries/_modules/tracking/snowplow/snowplowTracking';
import { trackMatchHubClick } from '../../../../libraries/_modules/tracking/helpers';

function HubMatchBlock() {
  const { mediaQueries, t, updateModalOptions, urlList, apiList, videoTags } = useReactAppState();
  const {
    remindMeUrl
  } = urlList || {};
  const { isMediumLargeMin } = mediaQueries || {};
  const { d3SportsAPI } = apiList || {};
  const {
    slug,
    mediaProviders,
    matchDateTime,
    matchKickOffTime,
    status,
    minuteDisplay,
    promoInfo,
    homeClubData,
    awayClubData,
    advertiser,
    isRecentGoal,
    count,
    period,
    venue,
    listType,
    isTimeTbd,
    competition,
    queryConfig,
    matchSportecId,
    winnerStatus,
    resultType,
    leg,
    forgeMatch,
    mlsMatch,
  } = useMatchData();

  const { match_information } = mlsMatch || {};

  const [homeSummary, setHomeSummary] = useState([]);
  const [awaySummary, setAwaySummary] = useState([]);

  const forgeMatchVideosEndpoint = forgeMatchVideosAPI({ matchOptaId: matchSportecId });
  const forgeMatchContentVideosEndpoint = forgeMatchContentVideosAPI({ matchSlug: forgeMatch?.slug });
  const d3PlayersEndpoint = d3PlayerAPI({ matchOptaId: matchSportecId });
  const d3AggregateEndpoint = d3AggregateAPI({ matchOptaId: matchSportecId });
  const summaryEndpoint = mlsMatchCommentariesAPI({
    matchOptaId: matchSportecId,
    includeOwnGoals: true,
    includeSecondYellowCards: true,
    includeYellowCards: false,
    includePenaltyGoals: true,
    includeRedCards: true,
    includeSubstitutions: false,
    includeGoals: true
  });

  const composedMinuteDisplay = leg && minuteDisplay
    ? `${composeLeg(leg, t)} - ${minuteDisplay}`
    : minuteDisplay
      ? minuteDisplay
      : leg
        ? leg
        : '';

  const { data: d3Players } = useQuery({
    queryKey: [`d3-players-match`, { matchSportecId }],
    queryFn: () => fetchClient({ endpoint: d3PlayersEndpoint, apiURL: d3SportsAPI }),
    ...queryConfig,
    enabled: !!matchSportecId
  });

  const { data: aggregateData } = useQuery({
    queryKey: [`d3-aggregate`, { matchSportecId }],
    queryFn: () => fetchClient({ endpoint: d3AggregateEndpoint, apiURL: d3SportsAPI }),
    ...queryConfig,
    enabled: !!matchSportecId && (competition?.matchSportecId == 549 || (match_information?.competition_id == 98 && match_information.competition_type === "Cup")),
  });

  const { data: forgeMatchVideos } = useQuery({
    queryKey: [`forge-match-videos`, { matchSportecId }],
    queryFn: () => fetchClient({ apiURL: apiURL.forgeDAPI, endpoint: forgeMatchVideosEndpoint }),
    ...queryConfig,
    enabled: status?.abbreviation === statusTypes.post,
    select: videos => videos?.items
  });

  const { data: forgeMatchContentVideos } = useQuery({
    queryKey: [`forge-match-content-videos`, { slug }],
    queryFn: () => fetchClient({ apiURL: apiURL.forgeDAPI, endpoint: forgeMatchContentVideosEndpoint }),
    ...queryConfig,
    enabled: status?.abbreviation === statusTypes.post,
    select: videos => videos?.items
  });

  const { aggregate, date: aggregateDate } = aggregateData || {};

  const composedAggregate = composeAggregate(aggregate, aggregateDate);

  const videoList = React.useMemo(() => {
    const matchVideos = forgeMatchVideos?.length >= 0 && forgeMatchContentVideos?.length >= 0 ? [...forgeMatchVideos, ...forgeMatchContentVideos] : [];
    return composeVideoList(matchVideos, videoTags);
  }, [forgeMatchVideos, forgeMatchContentVideos]);

  const gameHighlights = videoList.find(video => video?.fields?.highlightType === highlightTypes.gameHighlights || video?.tags.find(tag => tag?.slug === matchHighlightsTagSlug));
  const fullGame = videoList.find(video => video?.fields?.highlightType === highlightTypes.fullGame);
  const condensedGame = videoList.find(video => video?.fields?.highlightType === highlightTypes.condensedGame);

  const watchHighlightsLabel = t("match_watch_highlights");
  const watchLiveLabel = t("match_watch_live");
  const remindMeLabel = t("match_remind_me");
  const timeZoneLabel = t("match_timezone");
  const kickoffLabel = t("match_ko");

  const { data: summary } = useQuery({
    queryKey: [`mls-commentaries`, {
      matchSportecId,
      includeOwnGoals: true,
      includeSecondYellowCards: true,
      includeYellowCards: false,
      includePenaltyGoals: true,
      includeRedCards: true,
      includeSubstitutions: false,
      includeGoals: true
    }],
    queryFn: () => fetchClient({ endpoint: `https://stats-api.stg.mlssoccer.com/matches/${matchSportecId}/commentary` }),
    ...queryConfig,
  });

  const { localTime } = matchDateTime || {};
  const { localTime: koTime } = matchKickOffTime || {};
  const date = localTime != null ? format(localTime, "E, LLL d") : null;
  const localTimeFormatted = localTime != null ? format(localTime, "MM/dd/yy") : null;
  const timePeriod = localTime != null ? format(localTime, "a") : null;
  const timeSlice = localTime != null ? format(localTime, `p`).slice(0, -3) : null;

  const time = koTime != null
    ? format(koTime, `p`)
    : localTime != null
      ? format(localTime, `p`)
      : null;

  const {
    firstPartyTickets,
    thirdPartyTickets,
    sponsorImage,
    promotionalSponsor
  } = promoInfo || {};
  const { assetUrl } = sponsorImage || {};
  const { homeScore, awayScore, homeShootoutScore, awayShootoutScore, shootoutResult } = formatScores({
    homeClubData,
    awayClubData,
    period,
    resultType
  });

  const matchFinalLabel = leg
    ? `${composeLeg(leg, t)} - ${t("match_final")} ${localTimeFormatted ? localTimeFormatted : ""}`
    : `${t("match_final")} ${localTimeFormatted ? localTimeFormatted : ""}`;

  useEffect(() => {
    if (summary == null) {
      return;
    }
    const { home, away } = formatPlayerSummaryData({ home: homeClubData, away: awayClubData, summary, players: d3Players }) || {};

    setHomeSummary(home);
    setAwaySummary(away);
  }, [homeClubData, awayClubData, summary, d3Players]);

  const showTicketButton = firstPartyTickets?.url || thirdPartyTickets?.url;

  const trackButton = (type) => {
    const matchContext = createMatchContext(composeMLSMatchContextData(mlsMatch));

    const uiInteractionEvent = createUiInteractionPrimaryEvent({
      elementName: type,
      interactionType: 'click',
      elementType: 'button',
      elementPosition: "match header"
    });

    trackCustomSnowplowEvent(uiInteractionEvent, [matchContext]);
  };

  const handleClick = (e, label) => {
    e.preventDefault();

    trackButton(label);

    updateModalOptions({ type: modalActionTypes.modalClassName, payload: "mls-o-modal mls-o-modal--match" });
    updateModalOptions({ type: modalActionTypes.modalType, payload: modalTypes.animated });
    updateModalOptions({ type: modalActionTypes.label, payload: 'match modal' });
    updateModalOptions({ type: modalActionTypes.component, payload: MatchModal });
    updateModalOptions({ type: modalActionTypes.modalData, payload: { queryConfig, matchOptaId: matchSportecId, homeClubData, awayClubData, matchDateTime, venue, status, listType, mediaProviders, promoInfo, winnerStatus, slug, isTimeTbd } });
    updateModalOptions({ type: modalActionTypes.show });
  };

  return (
    <>
      <div className="mls-o-match-strip__match">
        <div className="mls-o-match-strip__header">
          <MatchInfo />
          <Sponsor
            hasPresBy={false}
            templateUrl={assetUrl}
            url={promotionalSponsor?.url}
            accessibleText={promotionalSponsor?.accessibleText}
            displayText={promotionalSponsor?.displayText}
            openInNewTab={promotionalSponsor?.openInNewTab}
          />
        </div>
        <div className="mls-o-match-strip__club-group">
          <HubMatchClub clubData={homeClubData} isHome summary={homeSummary} />
          {
            status?.abbreviation === statusTypes.abandoned ?
              <div className="mls-o-match-strip__club-separator">
                <div className="mls-o-match-strip__match-period mls-o-match-strip__match-period--aban">
                  {t("match_abandoned")}
                </div>
              </div> :
              status?.abbreviation === statusTypes.pre
                ? <div className="mls-o-match-strip__club-separator">
                  {date ? <div className="mls-o-match-strip__date">{date}</div> : null}
                  <div className="mls-o-match-strip__time-wrapper">
                    {
                      isTimeTbd
                        ? <div className="mls-o-match-strip__tbd">{t("tbd")?.toUpperCase()}</div>
                        : (
                          <>
                            {timeSlice ? <div className="mls-o-match-strip__time">{timeSlice}</div> : null}
                            {timePeriod ? <div className="mls-o-match-strip__time-period">{timePeriod}</div> : null}
                            {/* <div className="mls-o-match-strip__time-zone">{timeZoneLabel}</div> */}
                          </>
                        )
                    }
                  </div>
                </div>
                : status?.abbreviation === statusTypes.live || status?.abbreviation === statusTypes.post
                  ? (
                    <div className="mls-o-match-strip__club-separator">
                      <div className="mls-o-match-strip__match-period">
                        {
                          status?.abbreviation === statusTypes.live
                            ? (
                              <>
                                {advertiser ? <p className="mls-o-match-strip__advertiser">{advertiser}</p> : null}
                                <span className="mls-o-match-strip__pulse" />
                                <p className="mls-o-match-strip__minute">{composedMinuteDisplay}</p>
                              </>
                            )
                            : matchFinalLabel
                        }
                      </div>
                      <MatchScoreBox
                        isRecentGoal={isRecentGoal}
                        count={count}
                        awayScore={awayScore}
                        homeScore={homeScore}
                        homeShootoutScore={homeShootoutScore}
                        awayShootoutScore={awayShootoutScore}
                        isLive={status?.abbreviation === statusTypes.live} />
                    </div>
                  ) : (
                    <div className="mls-o-match-strip__club-separator">
                      <div className="mls-o-match-strip__other-result">
                        {status?.value}
                      </div>
                    </div>
                  )
          }
          <HubMatchClub clubData={awayClubData} isHome={false} summary={awaySummary} />
        </div>
        {
          status?.abbreviation === statusTypes.pre
            ? (
              <>
                {
                  !isTimeTbd
                    ? (
                      <div className="mls-o-match-strip__kick-off">
                        {`${kickoffLabel}: ${time ? time : ""}`}
                      </div>
                    )
                    : null
                }
                {
                  composedAggregate
                    ? <div className="mls-o-match-strip__additional-information">{composedAggregate}</div>
                    : null
                }
              </>
            ) : null
        }
        {
          !isMediumLargeMin
            ? (
              <div className="mls-o-match-strip__match-data-wrapper">
                <MatchData isHome summary={homeSummary} />
                <MatchData isHome={false} summary={awaySummary} />
              </div>
            ) : null
        }
        {
          composedAggregate && status?.abbreviation !== statusTypes.pre
            ? <div className="mls-o-match-strip__additional-information">{composedAggregate}</div>
            : null
        }
        <div className="mls-o-match-strip__cta-wrapper">
          {
            status?.abbreviation === statusTypes.pre
              ? (
                <>
                  {
                    showTicketButton
                      ? (
                        <ToggleButton
                          onClick={(e) => handleClick(e, 'tickets')}
                          alt={`Buy Tickets`}
                          className="mls-o-match-strip__cta">
                          <TicketIcon extraClasses={"mls-o-match-strip__tickets"} />
                          <span className="mls-o-match-strip__cta-label">{t("buy_tickets")}</span>
                        </ToggleButton>
                      )
                      : null
                  }
                  {
                    remindMeUrl
                      ? (
                        <ToggleButton
                          rel={`noopener noreferrer`}
                          alt={`Remind me`}
                          target="_blank"
                          onClick={() => {
                            trackMatchHubClick({
                              clickType: 'match header button',
                              clickItem: 'remind me',
                              match: slug,
                              matchState: status?.abbreviation,
                              competition: competition?.shortName
                            });
                          }}
                          href={remindMeUrl ? remindMeUrl : "#"}
                          className="mls-o-match-strip__cta mls-o-match-strip__cta--secondary">
                          <ReminderIcon extraClasses={"mls-o-match-strip__reminder"} />
                          <span className="mls-o-match-strip__cta-label">{remindMeLabel}</span>
                        </ToggleButton>
                      )
                      : null
                  }
                </>
              )
              : status?.abbreviation === statusTypes.live
                ? (
                  mediaProviders && mediaProviders.streamingList && mediaProviders.streamingList.length > 0
                    ? (
                      <ToggleButton
                        rel="noopener noreferrer"
                        className="mls-o-match-strip__cta"
                        onClick={(e) => handleClick(e, 'watch live')}
                      >
                        <PlayCircleIcon wrapperElement={"span"} extraClasses={"mls-o-match-strip__play-circle"} />
                        <span className="mls-o-match-strip__cta-label">{watchLiveLabel}</span>
                      </ToggleButton>
                    )
                    : null
                )
                : gameHighlights || condensedGame || fullGame
                  ? (
                    <ToggleButton
                      rel="noopener noreferrer"
                      className="mls-o-match-strip__cta mls-o-match-strip__cta--secondary"
                      onClick={(e) => handleClick(e, 'watch highlights')}
                    >
                      <PlayCircleIcon wrapperElement={"span"} extraClasses={"mls-o-match-strip__play-circle"} />
                      <span className="mls-o-match-strip__cta-label">{watchHighlightsLabel}</span>
                    </ToggleButton>
                  ) : null
          }
        </div>
      </div>
    </>
  );
}

// eslint-disable-next-line no-func-assign
HubMatchBlock = React.memo(HubMatchBlock);

export default HubMatchBlock;
